import React from "react";
import Image from "next/image";
import clsx from "clsx";
import MaxWidthWrapper from "components/Tiles/commonComponents/MaxWidthWrapper";
import { FadeIn } from "components/ui/FadeIn";
import DualButtonCTA, { ButtonVariant } from "components/ui/DualButtonCTA";

interface ComparisonItem {
  feature: string;
  ours: string | boolean;
  theirs: string | boolean;
}

interface ComparisonGroup {
  title: string;
  items: ComparisonItem[];
}

interface ButtonProps {
  buttonText: string;
  buttonUrl: string;
  buttonVariant: ButtonVariant;
}

interface ComparisonTableProps {
  title?: string;
  subtitle?: string;
  miniTitle?: string;
  featureHeader?: string;
  ourHeader?: {
    text?: string;
    logo?: {
      src: string;
      alt: string;
      width: number;
      height: number;
    };
  };
  theirHeader?: {
    text?: string;
    logo?: {
      src: string;
      alt: string;
      width: number;
      height: number;
    };
  };
  comparisonData?: (ComparisonItem | ComparisonGroup)[];
  className?: string;
  wrapperClass?: string;
  ourColumnBgColor?: string;
  titleIcon?: {
    src: string;
    alt: string;
    width: number;
    height: number;
  } | null;
  bgColor?: string;
  button1?: ButtonProps;
  button2?: ButtonProps;
  buttonPadding?: string;
  buttonPosition?: "top" | "bottom";
  miniTitleTag?: React.ElementType;
  enableStickyHeader?: boolean;
}

const ComparisonTable: React.FC<ComparisonTableProps> = ({
  title,
  subtitle,
  miniTitle,
  ourHeader,
  theirHeader,
  comparisonData,
  className,
  wrapperClass,
  ourColumnBgColor,
  titleIcon,
  bgColor,
  button1,
  button2,
  buttonPadding,
  buttonPosition = "bottom",
  miniTitleTag: MiniTitleTag = "h3",
  enableStickyHeader = false
}) => {
  const ourColumnStyle = ourColumnBgColor ? { backgroundColor: ourColumnBgColor } : {};

  const ButtonComponent = (button1 || button2) && (
    <FadeIn>
      <div className={buttonPadding || ""}>
        <DualButtonCTA button1={button1} button2={button2} />
      </div>
    </FadeIn>
  );

  const renderTableRows = (data: (ComparisonItem | ComparisonGroup)[]) => {
    return data.map((item, index) => {
      if ("items" in item) {
        // This is a group
        return (
          <React.Fragment key={index}>
            <tr className="bg-gray-200">
              <td colSpan={3} className="border-b-2 border-gray-300 p-6 text-lg font-bold text-gray-800">
                {item.title}
              </td>
            </tr>
            {item.items.map((subItem, subIndex) => renderTableRow(subItem, `${index}-${subIndex}`))}
          </React.Fragment>
        );
      } else {
        // This is a regular item
        return renderTableRow(item, index);
      }
    });
  };

  const renderTableRow = (item: ComparisonItem, key: React.Key) => (
    <tr
      key={key}
      className={clsx(
        typeof key === "number" && key % 2 === 0 ? "bg-white" : "bg-gray-50",
        "transition-colors duration-200 ease-in-out hover:bg-gray-100"
      )}
    >
      <td className="border-t p-4 font-medium">{item.feature}</td>
      <td className="border-l border-r border-t p-4 text-center" style={ourColumnStyle}>
        {typeof item.ours === "boolean" ? (
          item.ours ? (
            "✅"
          ) : (
            "❌"
          )
        ) : (
          <span className="font-semibold text-green-600">{item.ours}</span>
        )}
      </td>
      <td className="border-t p-4 text-center">
        {typeof item.theirs === "boolean" ? (
          item.theirs ? (
            "✅"
          ) : (
            "❌"
          )
        ) : (
          <span className="font-semibold text-red-600">{item.theirs}</span>
        )}
      </td>
    </tr>
  );

  return (
    <section id="comparison-table" className={clsx("w-full", className)} style={{ backgroundColor: bgColor }}>
      <MaxWidthWrapper className={wrapperClass}>
        <FadeIn>
          <div className="mb-8 flex flex-col items-center justify-center">
            <div className="items-center justify-center align-middle">
              <div className="tile-title-container">
                {titleIcon && (
                  <Image
                    src={titleIcon.src}
                    alt={titleIcon.alt}
                    width={titleIcon.width}
                    height={titleIcon.height}
                    className="relative mb-4 inline"
                  />
                )}
                {miniTitle && (
                  <MiniTitleTag className="subby mb-2 text-center text-sm font-semibold uppercase tracking-wide text-gray-600">
                    {miniTitle}
                  </MiniTitleTag>
                )}
                {title && (
                  <h2 className="tile-main-header mb-4 text-center font-heading text-3xl font-bold [text-wrap:balance]">
                    {title}
                  </h2>
                )}
                {subtitle && <p className="tile-subtitle mx-auto max-w-2xl text-center text-gray-600">{subtitle}</p>}
              </div>
            </div>
          </div>

          {buttonPosition === "top" && ButtonComponent}

          <div className="relative mt-4 w-full">
            <div
              className="pointer-events-none absolute inset-0"
              style={{
                boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)",
                borderRadius: "0.5rem",
                left: "33.333333%"
              }}
            ></div>
            <div
              className={clsx(
                "relative overflow-x-auto rounded-lg scrollbar-hide",
                enableStickyHeader ? "max-h-[600px] overflow-y-auto" : "overflow-y-hidden"
              )}
            >
              <table className="w-full border-collapse">
                <thead className={clsx(enableStickyHeader && "sticky top-0 z-10")}>
                  <tr>
                    <th className="w-1/3 bg-transparent"></th>
                    <th
                      className="w-1/3 rounded-tl-lg bg-gray-800 p-4 text-center font-semibold text-white lg:text-lg"
                      style={ourColumnStyle}
                    >
                      {ourHeader?.logo ? (
                        <Image
                          src={ourHeader.logo.src}
                          alt={ourHeader.logo.alt}
                          width={ourHeader.logo.width}
                          height={ourHeader.logo.height}
                          className="mx-auto"
                        />
                      ) : (
                        ourHeader?.text
                      )}
                    </th>
                    <th className="w-1/3 rounded-tr-lg bg-gray-800 p-4 text-center text-lg font-semibold text-white">
                      {theirHeader?.logo ? (
                        <Image
                          src={theirHeader.logo.src}
                          alt={theirHeader.logo.alt}
                          width={theirHeader.logo.width}
                          height={theirHeader.logo.height}
                          className="mx-auto"
                        />
                      ) : (
                        theirHeader?.text
                      )}
                    </th>
                  </tr>
                </thead>
                <tbody>{renderTableRows(comparisonData || [])}</tbody>
              </table>
            </div>
          </div>

          {buttonPosition === "bottom" && ButtonComponent}
        </FadeIn>
      </MaxWidthWrapper>
    </section>
  );
};

export default ComparisonTable;
